import { useState, useEffect, useRef } from 'react';

/**
 * Tool data structure for testing.
 */
interface ToolData {
  id: string;
  name: string;
  description: string | null;
  toolType: string;
  configSchema?: unknown;
  status: string | null;
  createdAt: string | null;
}

/**
 * Test result from the AI service.
 */
interface TestResult {
  response: string;
  toolName: string;
  model: string;
  usage: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

interface TestToolModalProps {
  isOpen: boolean;
  tool: ToolData | null;
  onClose: () => void;
  onTest: (data: { toolId: string; input: string }) => Promise<TestResult>;
  isSubmitting?: boolean;
  error?: string;
}

/**
 * Modal component for testing a prompt-based tool.
 *
 * Provides a form for administrators to:
 * - Enter sample input text
 * - Execute the tool's prompt against the input
 * - View the AI's response
 */
export function TestToolModal({
  isOpen,
  tool,
  onClose,
  onTest,
  isSubmitting = false,
  error,
}: TestToolModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [input, setInput] = useState('');
  const [result, setResult] = useState<TestResult | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen && tool) {
      setInput('');
      setResult(null);
      setValidationError(null);
    }
  }, [isOpen, tool]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle click outside modal to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  if (!isOpen || !tool) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);
    setResult(null);

    // Validate input
    if (!input.trim()) {
      setValidationError('Test input is required');
      return;
    }

    try {
      const testResult = await onTest({ toolId: tool.id, input: input.trim() });
      setResult(testResult);
    } catch {
      // Error handling is done by parent component
    }
  };

  const getPromptDefinition = (): string | null => {
    if (tool.toolType !== 'prompt-based' || !tool.configSchema) return null;
    const schema = tool.configSchema as { promptDefinition?: string };
    return schema.promptDefinition || null;
  };

  const promptDefinition = getPromptDefinition();
  const isValid = input.trim();

  return (
    <div style={styles.overlay}>
      <div ref={modalRef} style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <span style={styles.headerIcon}>&#128300;</span>
            <div>
              <h2 style={styles.title}>Test Tool</h2>
              <p style={styles.subtitle}>Test "{tool.name}" with sample input</p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={styles.closeButton}
            aria-label="Close modal"
          >
            &#215;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.content}>
            {/* Error Display */}
            {(validationError || error) && (
              <div style={styles.errorMessage}>
                {validationError || error}
              </div>
            )}

            {/* Info Box showing prompt definition */}
            {promptDefinition && (
              <div style={styles.infoBox}>
                <div style={styles.infoIcon}>&#128161;</div>
                <div style={styles.infoContent}>
                  <p style={styles.infoTitle}>Prompt Definition</p>
                  <p style={styles.infoText}>
                    {promptDefinition.length > 200
                      ? `${promptDefinition.slice(0, 200)}...`
                      : promptDefinition
                    }
                  </p>
                </div>
              </div>
            )}

            {/* Input Field */}
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="test-tool-input">
                Test Input <span style={styles.required}>*</span>
              </label>
              <textarea
                id="test-tool-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Enter sample text to test the tool with..."
                style={styles.textarea}
                rows={4}
                disabled={isSubmitting}
                autoFocus
              />
              <span style={styles.helpText}>
                Enter the text you want the tool to process
              </span>
            </div>

            {/* Result Section */}
            {result && (
              <div style={styles.resultSection}>
                <p style={styles.resultLabel}>AI Response</p>
                <div style={styles.resultContainer}>
                  <pre style={styles.resultText}>{result.response}</pre>
                </div>
                <div style={styles.usageInfo}>
                  <span style={styles.usageItem}>
                    Model: <strong>{result.model}</strong>
                  </span>
                  <span style={styles.usageItem}>
                    Tokens: <strong>{result.usage.totalTokens}</strong>
                    {' '}(prompt: {result.usage.promptTokens}, completion: {result.usage.completionTokens})
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={styles.footer}>
            <button
              type="button"
              onClick={onClose}
              style={styles.cancelButton}
              disabled={isSubmitting}
            >
              Close
            </button>
            <button
              type="submit"
              style={{
                ...styles.submitButton,
                ...((!isValid || isSubmitting) ? styles.submitButtonDisabled : {}),
              }}
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span style={styles.spinner} />
                  Testing...
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M1 7L5 11L13 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Run Test
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '640px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  headerIcon: {
    fontSize: '24px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
    fontFamily: '"Sentient", Georgia, serif',
  },
  subtitle: {
    fontSize: '13px',
    color: '#9B9285',
    margin: 0,
  },
  closeButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    border: 'none',
    background: 'rgba(93, 86, 78, 0.06)',
    cursor: 'pointer',
    color: '#706A62',
    fontSize: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.15s ease',
  },
  content: {
    padding: '24px',
    overflowY: 'auto',
    flex: 1,
  },
  errorMessage: {
    padding: '12px 16px',
    backgroundColor: 'rgba(139, 90, 80, 0.1)',
    color: '#8B5A50',
    borderRadius: '10px',
    fontSize: '13px',
    marginBottom: '20px',
  },
  infoBox: {
    display: 'flex',
    gap: '12px',
    padding: '14px 16px',
    background: 'linear-gradient(135deg, rgba(90, 106, 138, 0.08) 0%, rgba(90, 138, 94, 0.08) 100%)',
    borderRadius: '10px',
    border: '1px solid rgba(90, 106, 138, 0.15)',
    marginBottom: '20px',
  },
  infoIcon: {
    fontSize: '18px',
    flexShrink: 0,
  },
  infoContent: {
    flex: 1,
    overflow: 'hidden',
  },
  infoTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#5A6A8A',
    margin: '0 0 4px 0',
  },
  infoText: {
    fontSize: '11px',
    color: '#706A62',
    lineHeight: '1.5',
    margin: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '8px',
  },
  required: {
    color: '#8B5A50',
  },
  textarea: {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '10px',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
    resize: 'vertical',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s ease',
  },
  helpText: {
    display: 'block',
    fontSize: '11px',
    color: '#9B9285',
    marginTop: '6px',
  },
  resultSection: {
    marginTop: '20px',
    padding: '16px',
    background: 'rgba(90, 138, 94, 0.06)',
    borderRadius: '12px',
    border: '1px solid rgba(90, 138, 94, 0.15)',
  },
  resultLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#5A8A5E',
    margin: '0 0 12px 0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  resultContainer: {
    padding: '12px',
    background: 'white',
    borderRadius: '8px',
    border: '1px solid rgba(90, 138, 94, 0.1)',
    maxHeight: '200px',
    overflow: 'auto',
  },
  resultText: {
    fontSize: '13px',
    color: '#3D3A36',
    lineHeight: '1.6',
    margin: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'inherit',
  },
  usageInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid rgba(90, 138, 94, 0.15)',
  },
  usageItem: {
    fontSize: '11px',
    color: '#706A62',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid rgba(93, 86, 78, 0.08)',
    flexShrink: 0,
  },
  cancelButton: {
    padding: '12px 20px',
    backgroundColor: 'white',
    color: '#5C564E',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
  },
  submitButton: {
    padding: '12px 20px',
    backgroundColor: '#5A8A5E',
    color: '#FAF9F7',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.15s ease',
  },
  submitButtonDisabled: {
    backgroundColor: '#9B9285',
    cursor: 'not-allowed',
  },
  spinner: {
    width: '14px',
    height: '14px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: 'white',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
};

export default TestToolModal;
